import { EditOutlined, PlusOutlined, ShopOutlined, SwapOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Checkbox,
    Form,
    Input,
    Modal,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebugData } from '../contexts/DebugDataContext';
import {
    type CashPointStation,
    createFailureKey,
    isThisComputersStation,
    normalizeStationName,
    renameFailureKey,
    STATION_NAME_MAX_LENGTH,
    STATION_NAME_STORAGE_KEY,
    stationNameProblem,
    stationTypeFailureKey,
    storedStationNameAfterRename,
} from '../domain/cashPointStations';
import useRequest from '../hooks/useRequest';

const { Text, Title } = Typography;

interface RenameResponse {
    success: boolean;
    data: { cashPoint: CashPointStation; previousName: string };
}

/** The answer of creating a station and of changing its type. */
interface StationResponse {
    success: boolean;
    data: { cashPoint: CashPointStation };
}

const readStoredStationName = (): string | null => {
    try {
        return localStorage.getItem(STATION_NAME_STORAGE_KEY);
    } catch {
        return null;
    }
};

/**
 * This browser starts again as `stationName`: the startup lookup in `App` then resolves the
 * station, its cash session, balance and first-time cash setup exactly as on a computer
 * that had always carried the name. A reload rather than patching the running contexts, so
 * nothing from the previous station survives in memory.
 */
const moveThisComputerTo = (stationName: string) => {
    localStorage.setItem(STATION_NAME_STORAGE_KEY, stationName);
    window.location.reload();
};

/**
 * Administration of the station registry: switches this computer to another station,
 * creates a station, and renames one keeping its id and history. Needed when a defective
 * computer is replaced, or when moving over from the old application, since both identify
 * the workstation by this name.
 */
export default function CashPointStations() {
    const { t } = useTranslation();
    const request = useRequest();
    const { updateCashPointData } = useDebugData();

    const [stations, setStations] = useState<CashPointStation[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [storedStationName, setStoredStationName] = useState(readStoredStationName);
    const [editing, setEditing] = useState<CashPointStation | null>(null);
    const [typedName, setTypedName] = useState('');
    const [saving, setSaving] = useState(false);
    const [switchTarget, setSwitchTarget] = useState<CashPointStation | null>(null);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newIsCashPoint, setNewIsCashPoint] = useState(true);
    const [useNewOnThisComputer, setUseNewOnThisComputer] = useState(true);
    const [typeTarget, setTypeTarget] = useState<CashPointStation | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const response = await request<{ success: boolean; data: CashPointStation[] }>({
                path: '/cash-drawer/stations',
            });
            setStations(response.data ?? []);
            setLoadFailed(false);
        } catch (error) {
            console.error('Error loading stations:', error);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const openRename = (station: CashPointStation) => {
        setEditing(station);
        setTypedName(station.name);
    };

    const closeRename = () => {
        setEditing(null);
        setTypedName('');
    };

    const problem = editing === null ? null : stationNameProblem(typedName, editing.id, stations);
    const unchanged = editing !== null && normalizeStationName(typedName) === editing.name;

    const handleRename = async () => {
        if (editing === null || problem !== null || unchanged) {
            return;
        }

        setSaving(true);
        try {
            const response = await request<RenameResponse>({
                path: `/cash-drawer/stations/${editing.id}/name`,
                method: 'PUT',
                params: { name: normalizeStationName(typedName) },
            });
            const { cashPoint, previousName } = response.data;

            const nextStoredName = storedStationNameAfterRename(
                storedStationName,
                previousName,
                cashPoint.name,
            );
            if (nextStoredName !== null && nextStoredName !== storedStationName) {
                // This browser was using the renamed station: keep it matching at next start.
                localStorage.setItem(STATION_NAME_STORAGE_KEY, nextStoredName);
                setStoredStationName(nextStoredName);
                updateCashPointData(cashPoint.id, cashPoint.name);
            }

            message.success(
                t('configuration.stations.renamed', { from: previousName, to: cashPoint.name }),
            );
            closeRename();
            await refresh();
        } catch (error) {
            console.error('Error renaming station:', error);
            message.error(t(`configuration.stations.${renameFailureKey(error)}`));
        } finally {
            setSaving(false);
        }
    };

    const openCreate = () => {
        setNewName('');
        setNewIsCashPoint(true);
        setUseNewOnThisComputer(true);
        setCreating(true);
    };

    const createProblem = creating ? stationNameProblem(newName, null, stations) : null;

    const handleCreate = async () => {
        if (!creating || createProblem !== null) {
            return;
        }

        setSaving(true);
        try {
            const response = await request<StationResponse>({
                path: '/cash-drawer/stations',
                method: 'POST',
                params: { name: normalizeStationName(newName), isCashPoint: newIsCashPoint },
            });
            const { cashPoint } = response.data;

            if (useNewOnThisComputer) {
                moveThisComputerTo(cashPoint.name);
                return;
            }

            message.success(t('configuration.stations.created', { name: cashPoint.name }));
            setCreating(false);
            await refresh();
        } catch (error) {
            console.error('Error creating station:', error);
            message.error(t(`configuration.stations.${createFailureKey(error)}`));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Turns the station in `typeTarget` into a cash register, or a register into a workstation.
     * On this computer's own station the application starts again, so the session, balance and
     * first-time cash setup follow the new type at once.
     */
    const handleTypeChange = async () => {
        if (typeTarget === null) {
            return;
        }

        const makeCashPoint = !typeTarget.isCashPoint;
        setSaving(true);
        try {
            const response = await request<StationResponse>({
                path: `/cash-drawer/stations/${typeTarget.id}/cash-point`,
                method: 'PUT',
                params: { isCashPoint: makeCashPoint },
            });
            const { cashPoint } = response.data;

            if (isThisComputersStation(cashPoint, storedStationName)) {
                moveThisComputerTo(cashPoint.name);
                return;
            }

            message.success(
                t(
                    makeCashPoint
                        ? 'configuration.stations.nowCashDrawer'
                        : 'configuration.stations.nowWorkstation',
                    { name: cashPoint.name },
                ),
            );
            setTypeTarget(null);
            await refresh();
        } catch (error) {
            console.error('Error changing station type:', error);
            message.error(t(`configuration.stations.${stationTypeFailureKey(error)}`));
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: t('configuration.stations.name'),
            dataIndex: 'name',
            key: 'name',
            render: (name: string, station: CashPointStation) => (
                <Space>
                    <Text strong>{name}</Text>
                    {isThisComputersStation(station, storedStationName) && (
                        <Tag color="blue" data-testid="this-computer-tag">
                            {t('configuration.stations.thisComputer')}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: t('configuration.stations.id'),
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: t('configuration.stations.type'),
            key: 'type',
            render: (_unused: unknown, station: CashPointStation) => (
                <Space>
                    <Switch
                        size="small"
                        checked={station.isCashPoint}
                        onChange={() => setTypeTarget(station)}
                        aria-label={t('configuration.stations.isCashPointSwitch')}
                        data-testid={`station-is-cash-point-${station.id}`}
                    />
                    {station.isCashPoint ? (
                        <Tag color="green">{t('configuration.stations.cashDrawer')}</Tag>
                    ) : (
                        <Tag>{t('configuration.stations.workstation')}</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: '',
            key: 'actions',
            render: (_unused: unknown, station: CashPointStation) => (
                <Space>
                    {!isThisComputersStation(station, storedStationName) && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<SwapOutlined />}
                            onClick={() => setSwitchTarget(station)}
                            data-testid={`use-station-${station.id}`}
                        >
                            {t('configuration.stations.useHere')}
                        </Button>
                    )}
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openRename(station)}
                        data-testid={`rename-station-${station.id}`}
                    >
                        {t('configuration.stations.rename')}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                    <Title level={4} style={{ marginBottom: 4 }}>
                        <ShopOutlined style={{ marginRight: 8 }} />
                        {t('configuration.stations.title')}
                    </Title>
                    <Text type="secondary">{t('configuration.stations.description')}</Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreate}
                    data-testid="create-station"
                >
                    {t('configuration.stations.create')}
                </Button>
            </Space>

            {loadFailed && (
                <Alert type="error" showIcon message={t('configuration.stations.loadFailed')} />
            )}

            <Table
                rowKey="id"
                data-testid="stations-table"
                loading={loading}
                dataSource={stations}
                columns={columns}
                pagination={false}
                locale={{ emptyText: t('configuration.stations.empty') }}
            />

            <Modal
                open={switchTarget !== null}
                title={t('configuration.stations.useHereTitle', { name: switchTarget?.name ?? '' })}
                okText={t('configuration.stations.useHereConfirm')}
                cancelText={t('common.cancel')}
                onOk={() => switchTarget && moveThisComputerTo(switchTarget.name)}
                onCancel={() => setSwitchTarget(null)}
                destroyOnHidden
            >
                <Alert
                    type="warning"
                    showIcon
                    message={t('configuration.stations.useHereWarning', {
                        name: switchTarget?.name ?? '',
                    })}
                />
            </Modal>

            <Modal
                open={typeTarget !== null}
                title={t(
                    typeTarget?.isCashPoint
                        ? 'configuration.stations.makeWorkstationTitle'
                        : 'configuration.stations.makeCashDrawerTitle',
                    { name: typeTarget?.name ?? '' },
                )}
                okText={t('configuration.stations.typeChangeConfirm')}
                cancelText={t('common.cancel')}
                onOk={handleTypeChange}
                onCancel={() => setTypeTarget(null)}
                confirmLoading={saving}
                destroyOnHidden
            >
                <Alert
                    type="warning"
                    showIcon
                    data-testid="station-type-warning"
                    message={t(
                        typeTarget?.isCashPoint
                            ? 'configuration.stations.makeWorkstationWarning'
                            : 'configuration.stations.makeCashDrawerWarning',
                        { name: typeTarget?.name ?? '' },
                    )}
                />
            </Modal>

            <Modal
                open={creating}
                title={t('configuration.stations.createTitle')}
                okText={t('configuration.stations.createConfirm')}
                cancelText={t('common.cancel')}
                onOk={handleCreate}
                onCancel={() => setCreating(false)}
                confirmLoading={saving}
                okButtonProps={{ disabled: createProblem !== null }}
                destroyOnHidden
            >
                <Form layout="vertical">
                    <Form.Item
                        label={t('configuration.stations.name')}
                        htmlFor="station-create-name"
                        validateStatus={createProblem === null ? undefined : 'error'}
                        help={
                            createProblem === null
                                ? t('configuration.stations.newNameHelp')
                                : t(`configuration.stations.${createProblem}`)
                        }
                    >
                        <Input
                            id="station-create-name"
                            data-testid="station-create-name"
                            value={newName}
                            maxLength={STATION_NAME_MAX_LENGTH}
                            onChange={event => setNewName(event.target.value.toUpperCase())}
                            onPressEnter={handleCreate}
                        />
                    </Form.Item>
                    <Form.Item label={t('configuration.stations.cashDrawer')}>
                        <Space>
                            <Switch
                                checked={newIsCashPoint}
                                onChange={setNewIsCashPoint}
                                data-testid="station-create-is-cash-point"
                            />
                            <Text type="secondary">
                                {t('configuration.stations.isCashPointHint')}
                            </Text>
                        </Space>
                    </Form.Item>
                    <Checkbox
                        checked={useNewOnThisComputer}
                        onChange={event => setUseNewOnThisComputer(event.target.checked)}
                        data-testid="station-create-use-here"
                    >
                        {t('configuration.stations.createUseHere')}
                    </Checkbox>
                </Form>
            </Modal>

            <Modal
                open={editing !== null}
                title={t('configuration.stations.renameTitle', { name: editing?.name ?? '' })}
                okText={t('configuration.stations.save')}
                cancelText={t('common.cancel')}
                onOk={handleRename}
                onCancel={closeRename}
                confirmLoading={saving}
                okButtonProps={{ disabled: problem !== null || unchanged }}
                destroyOnHidden
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('configuration.stations.renameWarning')}
                />
                <Form layout="vertical">
                    <Form.Item
                        label={t('configuration.stations.newName')}
                        htmlFor="station-new-name"
                        validateStatus={problem === null ? undefined : 'error'}
                        help={
                            problem === null
                                ? t('configuration.stations.newNameHelp')
                                : t(`configuration.stations.${problem}`)
                        }
                    >
                        <Input
                            id="station-new-name"
                            data-testid="station-new-name"
                            value={typedName}
                            maxLength={STATION_NAME_MAX_LENGTH}
                            onChange={event => setTypedName(event.target.value.toUpperCase())}
                            onPressEnter={handleRename}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
}
